/** Page size for the Master Terminal k-way merge. Keep small: food thumbnails still cost. */
export const TIMELINE_PAGE_SIZE = 20;

function parseDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function eventTimestamp(row, keys = ['created_at']) {
  if (!row) return null;
  for (const key of keys) {
    const d = parseDate(row[key]);
    if (d) return d;
  }
  return null;
}

export function weightTimestamp(row) {
  const fromCreated = parseDate(row?.created_at);
  if (fromCreated) return fromCreated;
  if (!row?.measurement_date) return null;
  const rawTime = row.measurement_time ? String(row.measurement_time) : '12:00:00';
  const time = rawTime.length <= 5 ? `${rawTime}:00` : rawTime;
  return parseDate(`${row.measurement_date}T${time}`);
}

export function isDisplayableChatMessage(msg) {
  if (!msg) return false;
  if (msg.role === 'assistant') return msg.message != null || msg.content != null;
  if (msg.role === 'user') return true;
  // System "ANALYZED FOOD CONTEXT" dumps are replaced by food_log cards.
  return false;
}

export function toChatEvent(msg) {
  const timestamp = eventTimestamp(msg);
  if (!timestamp) return null;
  return {
    id: `chat:${msg.id}`,
    type: 'chat',
    timestamp,
    payload: {
      id: msg.id,
      role: msg.role,
      message: msg.message,
      content: msg.content,
      attachments: msg.attachments,
      topic: msg.topic,
      created_at: msg.created_at,
      sender: msg.role === 'user' ? 'user' : 'bot',
    },
  };
}

export function toFoodEvent(log) {
  const timestamp = eventTimestamp(log);
  if (!timestamp) return null;
  return {
    id: `food:${log.id}`,
    type: 'food',
    timestamp,
    payload: {
      id: log.id,
      meal_label: log.meal_label,
      total_calories: log.total_calories,
      total_protein_g: log.total_protein_g,
      total_carbs_g: log.total_carbs_g,
      total_fat_g: log.total_fat_g,
      plan_score: log.plan_score,
      health_score: log.health_score,
      image_url: log.image_url,
      has_image: log.has_image,
      log_date: log.log_date,
      created_at: log.created_at,
    },
  };
}

export function toWeightEvent(log) {
  const timestamp = weightTimestamp(log);
  if (!timestamp) return null;
  return {
    id: `weight:${log.id}`,
    type: 'weight',
    timestamp,
    payload: {
      id: log.id,
      weight_kg: log.weight_kg,
      body_fat_percentage: log.body_fat_percentage,
      measurement_date: log.measurement_date,
      notes: log.notes,
      created_at: log.created_at,
    },
  };
}

/**
 * Maps chat_messages, food_logs, and weight_logs into one TimelineEvent[] sorted oldest → newest.
 * Does not keep separate source arrays in the UI — this is the only feed shape.
 */
export function normalizeTimelineEvents({ messages = [], food_logs = [], weight_logs = [] } = {}) {
  const events = [];
  for (const msg of messages) {
    if (!isDisplayableChatMessage(msg)) continue;
    const event = toChatEvent(msg);
    if (event) events.push(event);
  }
  for (const log of food_logs) {
    const event = toFoodEvent(log);
    if (event) events.push(event);
  }
  for (const log of weight_logs) {
    const event = toWeightEvent(log);
    if (event) events.push(event);
  }
  events.sort((a, b) => {
    const delta = a.timestamp - b.timestamp;
    if (delta !== 0) return delta;
    return String(a.id).localeCompare(String(b.id));
  });
  return events;
}

export function mergeTimelineEvents(prev = [], next = []) {
  const map = new Map(prev.map((event) => [event.id, event]));
  for (const event of next) map.set(event.id, event);
  return Array.from(map.values()).sort((a, b) => {
    const delta = a.timestamp - b.timestamp;
    if (delta !== 0) return delta;
    return String(a.id).localeCompare(String(b.id));
  });
}

export function oldestCursor(events = []) {
  if (!events.length) return null;
  return events[0].timestamp.toISOString();
}

export function sourceErrorsFromPayload(payload) {
  const errors = payload?.errors || {};
  return {
    messages: errors.messages || null,
    food_logs: errors.food_logs || null,
    weight_logs: errors.weight_logs || null,
  };
}

export function anySourceError(errors) {
  return Boolean(errors?.messages || errors?.food_logs || errors?.weight_logs);
}

export function allSourcesFailed(errors, payload) {
  const emptyMessages = !(payload?.messages || []).length;
  const emptyFood = !(payload?.food_logs || []).length;
  const emptyWeight = !(payload?.weight_logs || []).length;
  return Boolean(errors?.messages && errors?.food_logs && errors?.weight_logs) && emptyMessages && emptyFood && emptyWeight;
}

export function computeHasMore(payload) {
  const hasMore = payload?.hasMore || {};
  return Boolean(hasMore.messages || hasMore.food_logs || hasMore.weight_logs);
}
